import { useCallback, useEffect } from 'react';

import useAsyncRequest from '../../../../../hooks/useAsyncRequest';
import { get } from '../../../../../redux/api';
import type { ProjectGroupRelease } from '../../../../../types';

const fetchRelease = (groupId: number, releaseId: number) => {
  return get<ProjectGroupRelease>(`/api/project_group/${groupId}/release/${releaseId}`);
};

/**
 * Hook for fetching a specific release group release.
 * Incoming props are strings, but they are coerced to numbers for the fetch.
 *
 * @param {string} groupIdStr - The group ID of the release.
 * @param {string} releaseIdStr - The release ID of the release.
 */
function useFetchRelease(groupIdStr: string, releaseIdStr: string) {
  const onFetchRelease = useCallback(async () => {
    const groupId = Number(groupIdStr);
    const releaseId = Number(releaseIdStr);

    //  Without a valid groupId and releaseId, there is nothing we can fetch.
    if (!Number.isFinite(groupId) || !Number.isFinite(releaseId)) {
      return {
        data: null,
      };
    }

    return fetchRelease(groupId, releaseId);
  }, [groupIdStr, releaseIdStr]);

  //  This will fetch once on mount, as well as whenever the groupId or releaseId changes.
  return useAsyncRequest(onFetchRelease);
}

/**
 * Wrapper for useFetchRelease that also calls a `callback` when the release is fetched.
 * This is useful for the Edit Release Page's state, which needs to update the filters
 * and the previously added projects when a release is fetched.
 */
export function useFetchReleaseWithCallback(
  groupIdStr: string,
  releaseIdStr: string,
  callback: (release: ProjectGroupRelease) => void,
) {
  const { value: release, errors: releaseErrors } = useFetchRelease(groupIdStr, releaseIdStr);

  useEffect(() => {
    if (release) {
      callback?.(release);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We ignore this to avoid an infinite render loop.
  }, [release]);

  return {
    release,
    releaseErrors,
  };
}

export default useFetchRelease;
